import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, tap } from 'rxjs'
import { environment } from '../../../../environments/environment'

export interface LogFile {
  name: string
  size: number
  modified_at: string
}

export interface LogEntry {
  timestamp: string
  env: string
  level: string
  message: string
  context: string
}

interface LogFilesResponse {
  status: number
  data: LogFile[]
  message: string
}

interface LogEntriesResponse {
  status: number
  data: LogEntry[]
  meta: {
    current_page: number
    per_page: number
    total: number
    last_page: number
  }
  message: string
}

@Injectable({
  providedIn: 'root',
})
export class LogService {
  private http = inject(HttpClient)
  private apiUrl = environment.apiUrl

  private files = new BehaviorSubject<LogFile[]>([])
  private entries = new BehaviorSubject<LogEntry[]>([])
  private total = new BehaviorSubject<number>(0)
  private loading = new BehaviorSubject<boolean>(false)
  private filesLoading = new BehaviorSubject<boolean>(false)

  public files$ = this.files.asObservable()
  public entries$ = this.entries.asObservable()
  public total$ = this.total.asObservable()
  public loading$ = this.loading.asObservable()
  public filesLoading$ = this.filesLoading.asObservable()

  getFiles() {
    this.filesLoading.next(true)
    return this.http.get<LogFilesResponse>(`${this.apiUrl}/logs/files`).pipe(
      tap((res) => {
        this.files.next(res.data)
        this.filesLoading.next(false)
      })
    )
  }

  getEntries(file: string, page = 1, perPage = 50, level?: string, search?: string) {
    this.loading.next(true)
    let params = new HttpParams()
      .set('file', file)
      .set('page', page)
      .set('perPage', perPage)
    if (level) params = params.set('level', level)
    if (search) params = params.set('search', search)

    return this.http
      .get<LogEntriesResponse>(`${this.apiUrl}/logs/entries`, { params })
      .pipe(
        tap((res) => {
          this.entries.next(res.data)
          this.total.next(res.meta.total)
          this.loading.next(false)
        })
      )
  }
}
